<template>
  <div class="my-4">
    <h3>Klauseln-Vergleich</h3>

    <div
      v-if="!loadCriterions || !isLoading"
      class="d-print-none"
    >
      Das Laden vieler Kriterien kann sehr lange dauern und ev. den Browser
      blockieren. Laden sie nur dann alle Krierien auf einmal wenn sie sich
      sicher sind.
      <v-autocomplete
        v-model="criterionsSelected"
        :filter="SearchCriterions"
        :items="criterionOptions"
        box
        clearable
        deletable-chips
        item-value="typeId"
        label="Klauseltyp"
        multiple
      >
        <template v-slot:selection="data">
          <v-chip
            :selected="data.selected"
            class="chip--select-multi"
            close
            @input="UnselectCriterion(data.item)"
          >
            {{ data.item.value }}
          </v-chip>
        </template>
        <template v-slot:item="data">
          <template v-if="data.item.path.match(/\//g).length == 1">
            <v-list-tile-title class="ml-4">
              {{ data.item.header }}
            </v-list-tile-title>
            <v-divider />
          </template>
          <template v-else-if="data.item.path.match(/\//g).length == 2">
            {{ data.item }}
          </template>
          <template>
            <v-list-tile-content>
              <v-list-tile-title>{{ data.item.value }}</v-list-tile-title>
            </v-list-tile-content>
          </template>
        </template>
      </v-autocomplete>
      <button
        :disabled="
          criterionsSelected == undefined || criterionsSelected.length <= 0
        "
        class="btn success d-inline mt-2"
        @click="
          loadCriterions = true;
          loadCriterionsForContracts();
        "
      >
        Ausgewählte Laden
      </button>
      <button
        class="btn success d-inline mt-2 ml-2"
        @click="
          loadCriterions = true;
          loadAllCriterionsForContracts();
        "
      >
        Alle Laden
      </button>
    </div>
    <div v-if="loadCriterions">
      <div
        v-if="isLoading"
        style="text-align: center"
      >
        {{ maxCriterions - criterionsToWaitFor }}/{{ maxCriterions }} Klauseln
        geladen
        <v-progress-linear
          :value="((maxCriterions - criterionsToWaitFor) / maxCriterions) * 100"
          class="w-50"
          style="left: 25%"
        />
      </div>
      <div v-else>
        <CriterionComparisonExport
          :csv-definition="csvDefinition"
          :csv-file-name="csvFileName"
          :loaded-contracts="contracts"
          :selected-language="selectedLanguage"
          :pdf-content="pdfDefinition"
          :pdf-file-name="pdfExportName"
          :selected-criterion-types="loadedCriterions"
          class="mt-4"
        />
        <div class="compareblaTableWrapper">
          <table
            :style="{ 'min-width': 600 * contractsCopy.length + 1 + 'px' }"
            class="table compareblaTable"
          >
            <thead>
              <tr>
                <td />
                <td
                  v-for="(contract, index) in contractsCopy"
                  :key="index"
                >
                  <router-link
                    :to="
                      '/contract-overview/' +
                        contract.GavNumber +
                        '/' +
                        contract.Version +
                        '/' +
                        contract.Edition
                    "
                  >
                    {{ contract.DisplayName }} ({{ contract.Version }}.{{
                      contract.Edition
                    }})
                  </router-link>
                </td>
              </tr>
            </thead>
            <tbody>
              <template v-for="(topLevel, topLevelindex) in criterionStructure">
                <tr
                  v-if="loadedCriterionHeaders.some((x) => x === topLevel.path)"
                  :key="topLevelindex"
                  class="topLevelConfig"
                >
                  <td>
                    {{ topLevel.value }}
                  </td>
                  <td
                    v-for="(contract, tenantIndex) in contractsCopy"
                    :key="tenantIndex"
                  />
                </tr>
                <template
                  v-for="(secondLevel, secondLevelIndex) in topLevel.subLevels"
                >
                  <tr
                    v-if="
                      loadedCriterionHeaders.some((x) => x === secondLevel.path)
                    "
                    :key="secondLevelIndex"
                    class="secondLevelConfig"
                  >
                    <td>
                      {{ secondLevel.value }}
                    </td>
                    <td
                      v-for="(contract, tenantIndex) in contractsCopy"
                      :key="tenantIndex"
                    />
                  </tr>
                  <template
                    v-for="(
                      criterion, criterionIndex
                    ) in secondLevel.criterions"
                  >
                    <tr
                      v-if="
                        loadedCriterions.some(
                          (x) => x.typeId === criterion.typeId,
                        )
                      "
                      :key="criterionIndex"
                    >
                      <td>
                        {{ criterion.value }}
                      </td>
                      <td
                        v-for="(contract, tenantIndex) in contractsCopy"
                        :key="tenantIndex"
                      >
                        <!-- eslint-disable vue/no-v-html -->
                        <div
                          class="criterionContent"
                          v-html="
                            GetCriterionValue(
                              contract.Edition,
                              criterion.typeId,
                              criterion.value,
                            )
                          "
                        />
                        <!-- eslint-enable vue/no-v-html -->
                      </td>
                    </tr>
                  </template>
                </template>
              </template>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { LoadAllCriterionConfigurations } from "@/services/criterion.management.service.js";
import { GetCriterion } from "@/services/contract.edition.criterion.service.js";
import CriterionComparisonExport from "./CriterionComparisonExport.vue";
import htmlToPdfmake from "html-to-pdfmake";
import htmlToFormattedText from "html-to-formatted-text";

export default {
  components: {
    CriterionComparisonExport,
  },
  props: {
    contracts: {
      type: Array,
      required: true,
    },
    selectedLanguage: {
      type: String,
      required: true,
    },
    fallbackLanguages: {
      type: Array,
      required: true,
    },
  },
  data() {
    return {
      isLoading: false,
      errorObject: null,
      allCriterionConfiguration: [],
      criterionStructure: [],
      criterionsSelected: [],
      loadedCriterions: [],
      loadedCriterionHeaders: [],
      maxCriterions: 1,
      criterionsToWaitFor: 0,
      waitForUpdate: 0,
      loadCriterions: false,
      pdfExport: [],
      pdfDefinition: [],
      csvDefinition: [],
      csvFileName: "export.csv",
      pdfExportName: "export.pdf",
    };
  },
  computed: {
    criterionsToLoad: function () {
      var allCriterions = [];
      this.criterionStructure.map((topLevel) => {
        topLevel.subLevels.map((subLevel) => {
          subLevel.criterions.map((criterion) => {
            allCriterions.push(criterion);
          });
        });
      });
      allCriterions = allCriterions.filter((x) =>
        this.criterionsSelected.includes(x.typeId),
      );
      return allCriterions;
    },

    criterionOptions: function () {
      var allOptions = [];
      (this.criterionStructure || []).map((topLevel) => {
        topLevel.header = topLevel.value;
        allOptions.push(topLevel);
        (topLevel.subLevels || []).map((subLevel) => {
          allOptions.push({ header: subLevel.value });
          (subLevel.criterions || []).map((criterion) => {
            allOptions.push(criterion);
          });
        });
        allOptions.push({ divider: true });
      });

      return allOptions;
    },

    contractsCopy: function () {
      let copy = JSON.parse(JSON.stringify(this.contracts));
      copy.sort((a, b) => {
        return a.Edition - b.Edition;
      });
      return copy;
    },
  },
  watch: {
    selectedLanguage: function () {
      if (!this.isLoading) {
        this.GeneratePdfDefinition();
        this.GenerateCsvDefinition();
      }
    },
  },
  created() {
    this.GetCriterionConfigurations();
  },
  methods: {
    UnselectCriterion(item) {
      const index = this.criterionsSelected.indexOf(item.typeId);
      if (index >= 0) {
        this.criterionsSelected.splice(index, 1);
      }
    },
    // eslint-disable-next-line
    SearchCriterions(item, queryText, itemText) {
      if (item.value) {
        return item.value
          .toLowerCase()
          .includes(queryText.toLowerCase().trim());
      }
    },
    GetCriterionValue(editionId, klauselTypeId, criterionName) {
      // Filter criterion based on klauselTypeId and editionId (if available)
      var criterion = this.loadedCriterions.filter((criterion) => {
        return (
          (criterion.editionId == editionId || !editionId) &&
          criterion.typeId == klauselTypeId
        );
      });

      var existingExportEntry = this.pdfExport.filter((e) => {
        return e.title == criterionName;
      });

      if (existingExportEntry.length == 0) {
        this.pdfExport.push({
          title: criterionName,
        });

        existingExportEntry = this.pdfExport.filter((e) => {
          return e.title == criterionName;
        });
      }

      // Check if the criterion exists and has valid data
      if (
        criterion.length > 0 &&
        criterion[0].data &&
        criterion[0].data.klauselContents
      ) {
        var criterionValueByLanguage = criterion[0].data.klauselContents.filter(
          (content) => {
            return content.language == this.selectedLanguage;
          },
        );

        // If no content is found for the selected language, handle fallback languages
        if (
          criterionValueByLanguage.length == 0 ||
          !criterionValueByLanguage[0].value
        ) {
          this.fallbackLanguages.forEach((fallbackLanguage) => {
            if (fallbackLanguage.isoCode == this.selectedLanguage) {
              criterionValueByLanguage =
                criterion[0].data.klauselContents.filter((content) => {
                  return (
                    fallbackLanguage.isoCodeFallbackLanguage1 ==
                    content.language
                  );
                });

              if (
                criterionValueByLanguage.length == 0 ||
                !criterionValueByLanguage[0].value
              ) {
                criterionValueByLanguage =
                  criterion[0].data.klauselContents.filter((content) => {
                    return (
                      fallbackLanguage.isoCodeFallbackLanguage2 ==
                      content.language
                    );
                  });
              }
            }
          });
        }

        // Return the value if found, regardless of the presence of editionId
        if (criterionValueByLanguage[0] && criterionValueByLanguage[0].value) {
          if (editionId) {
            existingExportEntry[0][editionId] =
              criterionValueByLanguage[0].value;
          } else {
            existingExportEntry[0]["undefinedEdition"] =
              criterionValueByLanguage[0].value; // Use a placeholder key for undefined editions
          }
          return criterionValueByLanguage[0].value;
        }

        // If no value is found, return an empty string
        if (editionId) {
          existingExportEntry[0][editionId] = "";
        } else {
          existingExportEntry[0]["undefinedEdition"] = "";
        }
        return "";
      } else {
        // If no criterion data is available
        if (editionId) {
          existingExportEntry[0][editionId] = "";
        } else {
          existingExportEntry[0]["undefinedEdition"] = "";
        }
        return ""; // Default return if no criterion data is available
      }
    },

    GetCriterionConfigurations() {
      this.isLoading = true;

      LoadAllCriterionConfigurations(
        (response) => {
          this.allCriterionConfiguration = JSON.parse(JSON.stringify(response));

          this.allCriterionConfiguration.map((config) => {
            config.isLoading = true;
          });

          this.criterionStructure = [];

          this.allCriterionConfiguration
            .filter((c) => {
              return (c.path.match(/\//g) || []).length == 1;
            })
            .map((config) => {
              var level = {
                path: config.path,
                value: config.value,
                subLevels: [],
              };

              this.allCriterionConfiguration
                .filter((sublevel) => {
                  return (
                    (sublevel.path.match(/\//g) || []).length == 2 &&
                    sublevel.path.indexOf(config.path) == 0
                  );
                })
                .map((subLevelConfig) => {
                  var subLevel = {
                    path: subLevelConfig.path,
                    value: subLevelConfig.value,
                    criterions: [],
                  };

                  this.allCriterionConfiguration
                    .filter((criterion) => {
                      return (
                        (criterion.path.match(/\//g) || []).length == 3 &&
                        criterion.path.indexOf(subLevelConfig.path) == 0
                      );
                    })
                    .map((criterionConfig) => {
                      var criterion = {
                        path: criterionConfig.path,
                        value: criterionConfig.value,
                        typeId: criterionConfig.klauselTypeId,
                      };

                      subLevel.criterions.push(criterion);
                    });

                  level.subLevels.push(subLevel);
                });

              this.criterionStructure.push(level);
            });
        },
        () => {},
        false,
      );
    },
    loadAllCriterionsForContracts() {
      this.criterionsSelected = this.criterionOptions.map((x) => x.typeId);
      this.loadCriterionsForContracts();
    },
    loadCriterionsForContracts() {
      this.isLoading = true;
      var allCriterions = this.criterionsToLoad;

      // DEV-Help!
      // // todo: remove this!!
      // // only use the first 20 criterions, it takes too long
      // allCriterions = allCriterions.slice(0, 20);

      this.criterionsToWaitFor =
        this.contractsCopy.length * allCriterions.length;
      this.maxCriterions = this.criterionsToWaitFor;

      this.loadedCriterionHeaders = [];
      this.loadedCriterions = [];

      this.criterionStructure.map((header) => {
        header.subLevels.map((sublevel) => {
          sublevel.criterions.map((criterion) => {
            if (this.criterionsSelected.some((x) => x == criterion.typeId)) {
              this.loadedCriterionHeaders.push(sublevel.path);
              this.loadedCriterionHeaders.push(header.path);
            }
          });
        });
      });

      allCriterions.map((criterion) => {
        this.contractsCopy.map((contract) => {
          if (criterion.typeId) {
            this.loadCriterion(criterion.typeId, contract);
          } else {
            this.criterionsToWaitFor--;
          }
        });
      });
    },
    loadCriterion(typeId, contract) {
      GetCriterion(
        contract.GavNumber,
        contract.Version,
        contract.Edition,
        typeId,
        (success) => {
          this.loadedCriterions.push({
            editionId: contract.Edition,
            typeId: typeId,
            data: success,
          });

          this.criterionsToWaitFor--;

          if (this.criterionsToWaitFor == 0) {
            this.isLoading = false;
            this.GeneratePdfDefinition();
            this.GenerateCsvDefinition();
          }
        },
        () => {
          this.criterionsToWaitFor--;
          if (this.criterionsToWaitFor == 0) {
            this.isLoading = false;
            this.GeneratePdfDefinition();
            this.GenerateCsvDefinition();
          }
        },
      );
    },
    GenerateCsvDefinition() {
      if (this.pdfExport.length > 0) {
        this.csvDefinition = "";
        var csvContent = [];
        this.pdfExport.map((row) => {
          var csvLine = [];
          csvLine.push(row.title);
          this.contractsCopy.map((contract) => {
            if (contract.Edition && row[contract.Edition]) {
              csvLine.push(
                this.replaceUmlaute(
                  htmlToFormattedText(
                    row[contract.Edition]
                      .replace(/"/g, '""')
                      .replace(/<br>/g, "\n"),
                  ),
                ),
              );
            } else {
              csvLine.push("");
            }
          });

          csvContent.push(csvLine);
        });
        this.csvDefinition = csvContent;
      }
      setTimeout(() => {
        this.$emit("csvGenerated", csvContent);
      }, 500);

      var headerRow = [];
      headerRow.push("");
      this.contractsCopy.forEach((x) => {
        headerRow.push(x.DisplayName);
      });
      csvContent.unshift(headerRow);
      this.csvDefinition = csvContent;
      this.csvFileName = `Klauselvergleich_${this.$moment(new Date()).format(
        "YYYYMMDD",
      )}.csv`;
    },
    GeneratePdfDefinition() {
      var numberofContract = this.contractsCopy.length;
      var tableDefinition = [];
      var headerRow = [];
      headerRow.push({ text: "" });
      this.contractsCopy.forEach((contract) => {
        headerRow.push({
          text: `${contract.DisplayName} (Edition: ${contract.Edition})`,
          fontSize: 10,
        });
      });
      tableDefinition.push(headerRow);

      this.criterionStructure
        .filter((x) =>
          this.criterionsToLoad.some((y) => y.path.startsWith(x.path)),
        )
        .forEach((topLevel) => {
          var topLevelRow = [];
          topLevelRow.push({
            text: topLevel.value,
            colSpan: numberofContract + 1,
            fontSize: 12,
          });
          topLevelRow.push(...Array(numberofContract).fill({ text: "" }));
          tableDefinition.push(topLevelRow);

          topLevel.subLevels
            .filter((x) =>
              this.criterionsToLoad.some((y) => y.path.startsWith(x.path)),
            )
            .forEach((subLevel) => {
              var subLevelRow = [];
              subLevelRow.push({
                text: subLevel.value,
                colSpan: numberofContract + 1,
                fontSize: 10,
              });
              subLevelRow.push(...Array(numberofContract).fill({ text: "" }));
              tableDefinition.push(subLevelRow);

              subLevel.criterions
                .filter((x) =>
                  this.criterionsToLoad.some((y) => y.typeId === x.typeId),
                )
                .forEach((criterion) => {
                  var criterionRow = [];
                  criterionRow.push({
                    text: criterion.value,
                  });

                  this.contractsCopy.forEach((contract) => {
                    var value = this.GetCriterionValue(
                      contract.Edition,
                      criterion.typeId,
                      criterion.value,
                    );

                    if (value === undefined || value.length === 0) {
                      criterionRow.push({ text: "" });
                    } else {
                      value = htmlToPdfmake(value.replace(/<br>/g, "\n"));
                      criterionRow.push({
                        stack: value,
                      });
                    }
                  });
                  tableDefinition.push(criterionRow);
                });
            });
        });

      var addStyletoTableElement = (partDefinition) => {
        partDefinition.map((x) => {
          if (x.table !== undefined) {
            x.style = "criterionTable";
          }
          if (x.stack !== undefined) {
            addStyletoTableElement(x.stack);
          }
        });
      };

      tableDefinition.forEach((row) => {
        addStyletoTableElement(row);
      });

      this.pdfDefinition = tableDefinition;
      this.pdfFileName = `Klauselvergleich_${this.$moment(new Date()).format(
        "YYYYMMDD",
      )}.pdf`;
      setTimeout(() => {
        this.$emit("pdfGenerated", this.pdfDefinition);
      }, 500);
    },
    replaceUmlaute(value) {
      value = value.replace(/&auml;/g, "ä");
      value = value.replace(/&ouml;/g, "ö");
      value = value.replace(/&uuml;/g, "ü");
      value = value.replace(/&szlig;/g, "ß");
      value = value.replace(/&Auml;/g, "Ä");
      value = value.replace(/&Ouml;/g, "Ö");
      value = value.replace(/&Uuml;/g, "Ü");
      return value;
    },
  },
};
</script>

<style>
.criterionContent table {
  overflow: auto;
  display: block;
  width: 100%;
}
</style>

<style scoped>
.compareblaTable thead tr td {
  position: sticky;
  top: 0;
  background-color: #dbdbdb !important;
}

.compareblaTable tbody tr td:first-child {
  position: -webkit-sticky;
  position: sticky;
  left: 0;
  z-index: 1;
  background-color: #505050;
  color: white;
}

.compareblaTable tbody tr td:nth-child(even) {
  background-color: #f7f7f7;
}

.compareblaTable tbody tr td {
  width: 500px;
}

.compareblaTable tbody .newSection td {
  background-color: #777777 !important;
}

.compareblaTable {
  table-layout: fixed;
  position: relative;
}

.compareblaTable .topLevelConfig td {
  background-color: #777777 !important;
  font-size: 20px;
}

.compareblaTable .secondLevelConfig td {
  background-color: #929292 !important;
  font-size: 15px;
}

.compareblaTableWrapper {
  position: relative;
  max-height: calc(100vh - 200px);
  overflow: scroll;
}

@media print {
  @page {
    size: landscape;
  }

  .compareblaTableWrapper {
    position: relative;
    overflow: auto;
    max-height: initial;
    width: 100%;
  }

  .compareblaTable {
    position: initial;
    width: 100%;
    min-width: 100% !important;
  }

  .compareblaTable tbody tr td {
    width: auto;
  }

  .compareblaTable thead tr td {
    position: initial;
    top: initial;
  }

  .compareblaTable tbody tr td:first-child {
    position: initial;
    position: initial;
    left: initial;
    color: black;
  }
}
</style>
